import { useEffect, useRef, useState } from "react";
import {
  Star,
  Settings,
  ChevronDown,
  Check,
  CheckCircle2,
  Clock,
  XCircle,
  PencilLine,
  RotateCcw,
} from "lucide-react";
import { ViolationBadge } from "@/components/audit/ViolationBadge";

type Status =
  | "drafting"
  | "submitted"
  | "under_review"
  | "removed"
  | "rejected"
  | "refunded";

type DemoReview = {
  id: string;
  productTitle: string;
  asin: string;
  rating: 1 | 2 | 3;
  bodySnippet: string;
  fullBody: string;
  violationType: string;
  verifiedPurchase: boolean;
  reviewerName: string;
  status: Status;
  statusUpdatedAt: string;
  rationale: string;
  timeline: { label: string; at: string; done: boolean }[];
};

type DemoActivity = { at: string; message: string };

type DemoBrand = {
  name: string;
  kpis: {
    productsMonitored: number;
    flaggedThisMonth: number;
    takedownsSubmitted: number;
    removed: number;
  };
  reviews: DemoReview[];
  activity: DemoActivity[];
};

type BrandId = "coffee" | "pet" | "outdoors";

const BRANDS: Record<BrandId, DemoBrand> = {
  coffee: {
    name: "Acme Coffee",
    kpis: {
      productsMonitored: 47,
      flaggedThisMonth: 14,
      takedownsSubmitted: 9,
      removed: 6,
    },
    reviews: [
      {
        id: "c1",
        productTitle: "Acme Coffee Mug 12oz",
        asin: "B0CFPJYX7P",
        rating: 2,
        bodySnippet:
          "Total scam, doesn't keep coffee hot at all. Buy XYZ Mugs instead — they're way better and cheaper at xyzmugs.com.",
        fullBody:
          "Total scam, doesn't keep coffee hot at all. Save your money and buy XYZ Mugs instead — they're way better and cheaper at xyzmugs.com. The XYZ ceramic line keeps coffee hot for 6+ hours, this thing barely makes it 30 minutes.",
        violationType: "competitor_review",
        verifiedPurchase: true,
        reviewerName: "J. Smith",
        status: "removed",
        statusUpdatedAt: "3 days ago",
        rationale:
          "Review primarily promotes a competitor product (XYZ Mugs) with a direct external link, which violates Amazon's Community Guidelines on promotional content and conflicts of interest.",
        timeline: [
          { label: "Flagged by Reputely", at: "Apr 14", done: true },
          { label: "Takedown submitted to Amazon", at: "Apr 14", done: true },
          { label: "Amazon removed the review", at: "Apr 17", done: true },
        ],
      },
      {
        id: "c2",
        productTitle: "Acme Stainless Tumbler 20oz",
        asin: "B0DMTNDHMY",
        rating: 1,
        bodySnippet:
          "I got this free in exchange for an honest review. Honestly it's pretty average...",
        fullBody:
          "I got this free in exchange for an honest review. Honestly it's pretty average, doesn't seal as tight as advertised. The lid leaks if you tilt it past 45 degrees. Wouldn't have paid for it.",
        violationType: "incentivized",
        verifiedPurchase: false,
        reviewerName: "M. Garcia",
        status: "submitted",
        statusUpdatedAt: "yesterday",
        rationale:
          "Reviewer explicitly discloses receiving the product free in exchange for the review, which violates Amazon's Community Guidelines prohibiting incentivized reviews from non-Vine reviewers.",
        timeline: [
          { label: "Flagged by Reputely", at: "May 7", done: true },
          { label: "Takedown submitted to Amazon", at: "May 7", done: true },
          { label: "Amazon review pending", at: "—", done: false },
        ],
      },
      {
        id: "c3",
        productTitle: "Acme Insulated Pour-Over",
        asin: "B0F2A1B3C4",
        rating: 3,
        bodySnippet:
          "Visit www.coffeeking.com for the real deal — these are knockoffs of their pour-over line.",
        fullBody:
          "Visit www.coffeeking.com for the real deal — these are knockoffs of their pour-over line. Email me at sarah.peters@gmail.com if you want photos comparing the two side by side.",
        violationType: "privacy_violation",
        verifiedPurchase: true,
        reviewerName: "K. Patel",
        status: "drafting",
        statusUpdatedAt: "2 hours ago",
        rationale:
          "Review contains personally identifiable information (email address) and external promotional URLs, violating Amazon's privacy and external-link policies.",
        timeline: [
          { label: "Flagged by Reputely", at: "May 8 · 6:14am", done: true },
          { label: "Drafting takedown request", at: "in progress", done: false },
          { label: "Submission pending", at: "—", done: false },
        ],
      },
      {
        id: "c4",
        productTitle: "Acme Travel Mug 16oz",
        asin: "B0G5H7I9J1",
        rating: 1,
        bodySnippet:
          "The seller refused to refund my order. Worst customer service ever, never buying from them again.",
        fullBody:
          "The seller refused to refund my order even though I asked within the return window. Worst customer service ever, never buying from them again. The mug itself is fine but I can't recommend a seller that won't honor their return policy.",
        violationType: "seller_feedback_misplaced",
        verifiedPurchase: true,
        reviewerName: "R. Lee",
        status: "under_review",
        statusUpdatedAt: "4 days ago",
        rationale:
          "Review focuses on seller customer-service experience rather than product quality. Seller feedback belongs in the Seller Feedback channel under Amazon's policy, not on the product detail page.",
        timeline: [
          { label: "Flagged by Reputely", at: "May 4", done: true },
          { label: "Takedown submitted to Amazon", at: "May 4", done: true },
          { label: "Under Amazon review", at: "in progress", done: false },
        ],
      },
      {
        id: "c5",
        productTitle: "Acme French Press 8 cup",
        asin: "B0K2L4M6N8",
        rating: 1,
        bodySnippet:
          "Skip this and buy the BrewMaster French Press instead.",
        fullBody:
          "Skip this and buy the BrewMaster French Press instead. It is cheaper, works better, and is the only one worth owning.",
        violationType: "off_topic",
        verifiedPurchase: false,
        reviewerName: "anonymous_8829",
        status: "removed",
        statusUpdatedAt: "1 week ago",
        rationale:
          "Review redirects shoppers to a competing product instead of evaluating the listed French press.",
        timeline: [
          { label: "Flagged by Reputely", at: "Apr 28", done: true },
          { label: "Takedown submitted to Amazon", at: "Apr 28", done: true },
          { label: "Amazon removed the review", at: "May 1", done: true },
        ],
      },
      {
        id: "c6",
        productTitle: "Acme Espresso Cups Set of 6",
        asin: "B0P9Q8R7S6",
        rating: 2,
        bodySnippet: "Got this discounted in a Facebook group review program.",
        fullBody:
          "Got this discounted in a Facebook group review program. Leaving 4 stars for the deal but honestly the cups are nothing special. Group admin asked for verified-purchase reviews so here's mine.",
        violationType: "incentivized",
        verifiedPurchase: false,
        reviewerName: "T. Nguyen",
        status: "rejected",
        statusUpdatedAt: "2 weeks ago",
        rationale:
          "Reviewer references receiving a discount through an external review group. Amazon's reviewer responded that the disclosure language was insufficient evidence to remove.",
        timeline: [
          { label: "Flagged by Reputely", at: "Apr 23", done: true },
          { label: "Takedown submitted to Amazon", at: "Apr 23", done: true },
          { label: "Amazon rejected — appealing", at: "Apr 24", done: true },
        ],
      },
      {
        id: "c7",
        productTitle: "Acme Cold Brew Carafe",
        asin: "B0R4S5T6U7",
        rating: 1,
        bodySnippet:
          "Mine arrived broken. Replacement was also broken. Going back to my old brand.",
        fullBody:
          "Mine arrived broken. Replacement was also broken. Going back to my old brand. The packaging was clearly insufficient for shipping a glass product. Avoid.",
        violationType: "review_swap",
        verifiedPurchase: true,
        reviewerName: "D. Khan",
        status: "refunded",
        statusUpdatedAt: "95 days ago",
        rationale:
          "Reviewer was identified in a known review-swap network. Amazon did not act within 90 days, so this takedown was refunded under our money-back guarantee.",
        timeline: [
          { label: "Flagged by Reputely", at: "Feb 1", done: true },
          { label: "Takedown submitted to Amazon", at: "Feb 1", done: true },
          { label: "90-day window elapsed — refunded", at: "May 2", done: true },
        ],
      },
    ],
    activity: [
      { at: "2h ago", message: "Submitted takedown for \"Acme Insulated Pour-Over\"" },
      { at: "Yesterday", message: "Submitted takedown for \"Acme Stainless Tumbler 20oz\"" },
      { at: "3d ago", message: "Amazon removed review on \"Acme Coffee Mug 12oz\"" },
      { at: "5d ago", message: "Flagged 3 new reviews across catalog" },
      { at: "1w ago", message: "Amazon removed review on \"Acme French Press 8 cup\"" },
    ],
  },
  pet: {
    name: "Acme Pet Co.",
    kpis: {
      productsMonitored: 23,
      flaggedThisMonth: 7,
      takedownsSubmitted: 5,
      removed: 4,
    },
    reviews: [
      {
        id: "p1",
        productTitle: "Acme Orthopedic Dog Bed XL",
        asin: "B0A1B2C3D4",
        rating: 1,
        bodySnippet:
          "I'm a competing brand owner. This bed is dangerous — the foam they use is non-CertiPUR.",
        fullBody:
          "I'm a competing brand owner. This bed is dangerous — the foam they use is non-CertiPUR and off-gasses for weeks. Our brand uses certified foam and we'd never sell something like this.",
        violationType: "conflict_of_interest",
        verifiedPurchase: false,
        reviewerName: "B. Martinez",
        status: "submitted",
        statusUpdatedAt: "2 days ago",
        rationale:
          "Reviewer self-identifies as a competing brand owner, creating a clear conflict of interest under Amazon's Community Guidelines.",
        timeline: [
          { label: "Flagged by Reputely", at: "May 6", done: true },
          { label: "Takedown submitted to Amazon", at: "May 6", done: true },
          { label: "Amazon review pending", at: "—", done: false },
        ],
      },
      {
        id: "p2",
        productTitle: "Acme Cat Scratching Tower",
        asin: "B0E5F6G7H8",
        rating: 3,
        bodySnippet:
          "Quality product. Fast shipping. Great value. Would recommend to a friend.",
        fullBody:
          "Quality product. Fast shipping. Great value. Would recommend to a friend. Excellent purchase experience overall.",
        violationType: "duplicate_or_template",
        verifiedPurchase: false,
        reviewerName: "anonymous_4421",
        status: "drafting",
        statusUpdatedAt: "1 hour ago",
        rationale:
          "This exact review template appears across 14 unrelated products from the same reviewer in the last 30 days — a strong template-review signal.",
        timeline: [
          { label: "Flagged by Reputely", at: "May 8 · 7:11am", done: true },
          { label: "Drafting takedown request", at: "in progress", done: false },
          { label: "Submission pending", at: "—", done: false },
        ],
      },
      {
        id: "p3",
        productTitle: "Acme No-Pull Dog Harness",
        asin: "B0I9J8K7L6",
        rating: 1,
        bodySnippet:
          "Got this for free from the Amazon Reviewer Club Discord. Honest 4-star review.",
        fullBody:
          "Got this for free from the Amazon Reviewer Club Discord. Honest 4-star review — straps are decent but the buckle feels cheap. Would buy at $20 but not at full price.",
        violationType: "incentivized",
        verifiedPurchase: false,
        reviewerName: "L. Wong",
        status: "removed",
        statusUpdatedAt: "5 days ago",
        rationale:
          "Reviewer explicitly discloses receiving the product free through an external group, which violates Amazon's incentivized-review policy.",
        timeline: [
          { label: "Flagged by Reputely", at: "May 3", done: true },
          { label: "Takedown submitted to Amazon", at: "May 3", done: true },
          { label: "Amazon removed the review", at: "May 3", done: true },
        ],
      },
      {
        id: "p4",
        productTitle: "Acme Stainless Pet Bowl",
        asin: "B0M5N4O3P2",
        rating: 2,
        bodySnippet:
          "Listing says 'Made in USA' but my bowl arrived stamped 'Made in China'.",
        fullBody:
          "Listing says 'Made in USA' but my bowl arrived stamped 'Made in China'. Either the listing is wrong or they're shipping a different product than advertised. Misleading.",
        violationType: "manufacturer_misrepresentation",
        verifiedPurchase: true,
        reviewerName: "S. Hill",
        status: "under_review",
        statusUpdatedAt: "3 days ago",
        rationale:
          "Reviewer alleges country-of-origin misrepresentation. Manufacturer evidence (supply documentation) submitted to Amazon for verification.",
        timeline: [
          { label: "Flagged by Reputely", at: "May 5", done: true },
          { label: "Takedown submitted to Amazon", at: "May 5", done: true },
          { label: "Under Amazon review", at: "in progress", done: false },
        ],
      },
      {
        id: "p5",
        productTitle: "Acme Cat Litter Mat",
        asin: "B0Q1R2S3T4",
        rating: 1,
        bodySnippet:
          "PetGuard makes a way better mat for half the price, available at petguard.com.",
        fullBody:
          "PetGuard makes a way better mat for half the price, available at petguard.com. This one sheds rubber bits everywhere and the size is misleading.",
        violationType: "competitor_review",
        verifiedPurchase: true,
        reviewerName: "A. Ortiz",
        status: "removed",
        statusUpdatedAt: "10 days ago",
        rationale:
          "Review primarily promotes a competing brand with an external URL — clear violation of Amazon's promotional and external-link policies.",
        timeline: [
          { label: "Flagged by Reputely", at: "Apr 28", done: true },
          { label: "Takedown submitted to Amazon", at: "Apr 28", done: true },
          { label: "Amazon removed the review", at: "Apr 29", done: true },
        ],
      },
      {
        id: "p6",
        productTitle: "Acme Squeaky Plush Toy",
        asin: "B0U5V6W7X8",
        rating: 1,
        bodySnippet: "F*** this seller. They never replied to my message.",
        fullBody:
          "F*** this seller. They never replied to my message about a missing piece. The toy is fine I guess but the experience was [profanity removed].",
        violationType: "off_topic",
        verifiedPurchase: true,
        reviewerName: "anonymous_9912",
        status: "rejected",
        statusUpdatedAt: "3 weeks ago",
        rationale:
          "Combination of profanity and off-topic seller-service complaint. Amazon initially rejected — appeal pending with stronger profanity evidence.",
        timeline: [
          { label: "Flagged by Reputely", at: "Apr 17", done: true },
          { label: "Takedown submitted to Amazon", at: "Apr 17", done: true },
          { label: "Amazon rejected — appealing", at: "Apr 18", done: true },
        ],
      },
    ],
    activity: [
      { at: "1h ago", message: "Drafting takedown for \"Acme Cat Scratching Tower\"" },
      { at: "2d ago", message: "Submitted takedown for \"Acme Orthopedic Dog Bed XL\"" },
      { at: "5d ago", message: "Amazon removed review on \"Acme No-Pull Dog Harness\"" },
      { at: "10d ago", message: "Amazon removed review on \"Acme Cat Litter Mat\"" },
      { at: "2w ago", message: "Flagged 2 new reviews across catalog" },
    ],
  },
  outdoors: {
    name: "Acme Outdoors",
    kpis: {
      productsMonitored: 89,
      flaggedThisMonth: 22,
      takedownsSubmitted: 14,
      removed: 11,
    },
    reviews: [
      {
        id: "o1",
        productTitle: "Acme 4-Person Tent",
        asin: "B0Y1Z2A3B4",
        rating: 1,
        bodySnippet:
          "I reviewed this tent and the seller asked me to review their other product in exchange.",
        fullBody:
          "I reviewed this tent and the seller asked me to review their other product in exchange for them reviewing mine. Got messaged on Amazon DMs. Quid pro quo review swap going on with this brand.",
        violationType: "review_swap",
        verifiedPurchase: true,
        reviewerName: "G. Foster",
        status: "submitted",
        statusUpdatedAt: "yesterday",
        rationale:
          "Reviewer alleges a quid-pro-quo review-swap arrangement, which violates Amazon's manipulation policy. Evidence package includes screenshots from a known seller-network forum.",
        timeline: [
          { label: "Flagged by Reputely", at: "May 7", done: true },
          { label: "Takedown submitted to Amazon", at: "May 7", done: true },
          { label: "Amazon review pending", at: "—", done: false },
        ],
      },
      {
        id: "o2",
        productTitle: "Acme 60L Cooler",
        asin: "B0C5D6E7F8",
        rating: 2,
        bodySnippet:
          "Listing claims 5-day ice retention but mine melted in 2. Material isn't roto-molded as advertised.",
        fullBody:
          "Listing claims 5-day ice retention but mine melted in 2. Material isn't roto-molded as advertised — looks like injection molded with a different feel. Either the listing is wrong or this is a different product than what was tested.",
        violationType: "manufacturer_misrepresentation",
        verifiedPurchase: true,
        reviewerName: "P. Singh",
        status: "removed",
        statusUpdatedAt: "6 days ago",
        rationale:
          "Reviewer alleges product specifications differ from listing. Manufacturer's certification documents and material spec sheet submitted as evidence.",
        timeline: [
          { label: "Flagged by Reputely", at: "May 1", done: true },
          { label: "Takedown submitted to Amazon", at: "May 1", done: true },
          { label: "Amazon removed the review", at: "May 2", done: true },
        ],
      },
      {
        id: "o3",
        productTitle: "Acme Headlamp 600 Lumens",
        asin: "B0G9H8I7J6",
        rating: 1,
        bodySnippet:
          "What a piece of [profanity]. The seller is a [profanity] who couldn't [profanity removed].",
        fullBody:
          "What a piece of [profanity]. The seller is a [profanity] who couldn't [profanity removed] their way out of a paper bag. Worst purchase of the year. Don't even bother.",
        violationType: "profanity_or_harassment",
        verifiedPurchase: true,
        reviewerName: "anonymous_5577",
        status: "drafting",
        statusUpdatedAt: "30 minutes ago",
        rationale:
          "Multiple instances of profanity directed at the seller. Violates Amazon's harassment policy and the off-topic seller-feedback policy.",
        timeline: [
          { label: "Flagged by Reputely", at: "May 8 · 7:42am", done: true },
          { label: "Drafting takedown request", at: "in progress", done: false },
          { label: "Submission pending", at: "—", done: false },
        ],
      },
      {
        id: "o4",
        productTitle: "Acme Trekking Poles Carbon",
        asin: "B0K1L2M3N4",
        rating: 3,
        bodySnippet:
          "Received this through a YouTube giveaway. The poles are okay, locks slip a bit on descents.",
        fullBody:
          "Received this through a YouTube giveaway from @hikingdaily. The poles are okay, locks slip a bit on descents. Gave them 3 stars to be honest about the issue.",
        violationType: "incentivized",
        verifiedPurchase: false,
        reviewerName: "C. Bauer",
        status: "under_review",
        statusUpdatedAt: "2 days ago",
        rationale:
          "Reviewer discloses receiving the product through a third-party giveaway, an incentivized-review violation under Amazon's policy.",
        timeline: [
          { label: "Flagged by Reputely", at: "May 6", done: true },
          { label: "Takedown submitted to Amazon", at: "May 6", done: true },
          { label: "Under Amazon review", at: "in progress", done: false },
        ],
      },
      {
        id: "o5",
        productTitle: "Acme Sleeping Bag 0°F",
        asin: "B0O5P6Q7R8",
        rating: 1,
        bodySnippet:
          "Reviewer's name and address: Mike Donovan, 422 Pine St, Boulder CO 80301. Seller delivered late.",
        fullBody:
          "Reviewer's name and address: Mike Donovan, 422 Pine St, Boulder CO 80301. Seller delivered late and the bag was damp on arrival. My phone is 720-555-0188 if anyone wants to discuss the issue.",
        violationType: "privacy_violation",
        verifiedPurchase: true,
        reviewerName: "M. Donovan",
        status: "removed",
        statusUpdatedAt: "2 weeks ago",
        rationale:
          "Review contains personal address, phone number, and full name — direct violation of Amazon's privacy policy on PII disclosure.",
        timeline: [
          { label: "Flagged by Reputely", at: "Apr 23", done: true },
          { label: "Takedown submitted to Amazon", at: "Apr 23", done: true },
          { label: "Amazon removed the review", at: "Apr 24", done: true },
        ],
      },
      {
        id: "o6",
        productTitle: "Acme Hiking Backpack 65L",
        asin: "B0S9T8U7V6",
        rating: 1,
        bodySnippet:
          "I manufacture a competing hiking pack. Buy ours instead.",
        fullBody:
          "I manufacture a competing hiking pack. This one is inferior, and shoppers should buy ours instead.",
        violationType: "competitor_review",
        verifiedPurchase: false,
        reviewerName: "anonymous_2238",
        status: "rejected",
        statusUpdatedAt: "1 month ago",
        rationale:
          "Reviewer expressly claims to manufacture a competing product and directs shoppers to buy it instead.",
        timeline: [
          { label: "Flagged by Reputely", at: "Apr 8", done: true },
          { label: "Takedown submitted to Amazon", at: "Apr 8", done: true },
          { label: "Amazon rejected — appealing", at: "Apr 10", done: true },
        ],
      },
    ],
    activity: [
      { at: "30m ago", message: "Drafting takedown for \"Acme Headlamp 600 Lumens\"" },
      { at: "Yesterday", message: "Submitted takedown for \"Acme 4-Person Tent\"" },
      { at: "2d ago", message: "Submitted takedown for \"Acme Trekking Poles Carbon\"" },
      { at: "6d ago", message: "Amazon removed review on \"Acme 60L Cooler\"" },
      { at: "2w ago", message: "Amazon removed review on \"Acme Sleeping Bag 0°F\"" },
    ],
  },
};

const STATUS_META: Record<
  Status,
  { label: string; cls: string; Icon: typeof Check }
> = {
  drafting: {
    label: "Drafting takedown",
    cls: "bg-blue-50 text-blue-700 ring-blue-200",
    Icon: PencilLine,
  },
  submitted: {
    label: "Submitted to Amazon",
    cls: "bg-amber-50 text-amber-800 ring-amber-200",
    Icon: Clock,
  },
  under_review: {
    label: "Under Amazon review",
    cls: "bg-amber-50 text-amber-800 ring-amber-200",
    Icon: Clock,
  },
  removed: {
    label: "Removed",
    cls: "bg-emerald-50 text-emerald-800 ring-emerald-200",
    Icon: CheckCircle2,
  },
  rejected: {
    label: "Rejected — appealing",
    cls: "bg-red-50 text-red-800 ring-red-200",
    Icon: XCircle,
  },
  refunded: {
    label: "Refunded · 90-day guarantee",
    cls: "bg-slate-100 text-slate-700 ring-slate-300",
    Icon: RotateCcw,
  },
};

export default function Demo() {
  const [brandId, setBrandId] = useState<BrandId>("coffee");
  const [expanded, setExpanded] = useState<string | null>(null);
  const brand = BRANDS[brandId];

  return (
    <div className="min-h-screen bg-slate-50">
      <TopBar />
      <main className="mx-auto w-full max-w-6xl px-6 py-10">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-slate-500">Brand</div>
            <BrandSelect value={brandId} onChange={(id) => { setBrandId(id); setExpanded(null); }} />
          </div>
          <div className="text-sm text-slate-500">
            Last sync · 4 minutes ago
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <KpiTile label="Products monitored" value={brand.kpis.productsMonitored} />
          <KpiTile label="Flagged this month" value={brand.kpis.flaggedThisMonth} />
          <KpiTile label="Takedowns submitted" value={brand.kpis.takedownsSubmitted} />
          <KpiTile label="Removed" value={brand.kpis.removed} accent />
        </div>

        <section className="mt-12">
          <div className="flex items-baseline justify-between">
            <h2 className="text-lg font-semibold text-slate-900">
              Recent flagged reviews
            </h2>
            <span className="text-sm text-slate-500">
              {brand.reviews.length} of {brand.kpis.flaggedThisMonth} this month
            </span>
          </div>

          <div className="mt-4 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white">
            {brand.reviews.map((r) => (
              <ReviewRow
                key={r.id}
                review={r}
                expanded={expanded === r.id}
                onToggle={() =>
                  setExpanded((cur) => (cur === r.id ? null : r.id))
                }
              />
            ))}
          </div>
        </section>

        <section className="mt-12">
          <h2 className="text-lg font-semibold text-slate-900">Live activity</h2>
          <ul className="mt-4 space-y-2 rounded-2xl border border-slate-200 bg-white p-5">
            {brand.activity.map((a, i) => (
              <li
                key={i}
                className="flex items-baseline gap-3 text-sm text-slate-700"
              >
                <span className="w-24 shrink-0 font-mono text-xs text-slate-500">
                  {a.at}
                </span>
                <span>{a.message}</span>
              </li>
            ))}
          </ul>
        </section>
      </main>
    </div>
  );
}

function TopBar() {
  return (
    <header className="border-b border-slate-200 bg-white">
      <div className="mx-auto flex h-14 w-full max-w-6xl items-center justify-between px-6">
        <div className="flex items-center gap-2 font-semibold tracking-tight text-slate-900">
          <span aria-hidden className="flex items-center gap-0.5">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star
                key={i}
                className="h-3 w-3 fill-emerald-500 text-emerald-500"
              />
            ))}
          </span>
          Reputely
        </div>
        <nav className="hidden items-center gap-7 text-sm md:flex">
          <span className="font-medium text-slate-900">Dashboard</span>
          <span className="text-slate-500 hover:text-slate-900 cursor-default">Catalog</span>
          <span className="text-slate-500 hover:text-slate-900 cursor-default">Activity</span>
          <span className="text-slate-500 hover:text-slate-900 cursor-default">Settings</span>
        </nav>
        <div className="flex items-center gap-3">
          <span className="hidden text-sm text-slate-600 sm:inline">acme@example.com</span>
          <Settings className="h-4 w-4 text-slate-500" />
        </div>
      </div>
    </header>
  );
}

function BrandSelect({
  value,
  onChange,
}: {
  value: BrandId;
  onChange: (v: BrandId) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  return (
    <div ref={ref} className="relative mt-1 inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-base font-semibold text-slate-900 hover:border-slate-400"
      >
        {BRANDS[value].name}
        <ChevronDown className="h-4 w-4 text-slate-500" />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-10 mt-1 w-56 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
          {(Object.keys(BRANDS) as BrandId[]).map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => {
                onChange(id);
                setOpen(false);
              }}
              className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
            >
              <span className="font-medium">{BRANDS[id].name}</span>
              {value === id && <Check className="h-4 w-4 text-emerald-600" />}
            </button>
          ))}
          <div className="border-t border-slate-100 px-3 py-2 text-sm text-slate-500 hover:bg-slate-50 cursor-default">
            + Add brand
          </div>
        </div>
      )}
    </div>
  );
}

function KpiTile({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: boolean;
}) {
  return (
    <div
      className={
        accent
          ? "rounded-2xl border border-emerald-200 bg-emerald-50 p-5"
          : "rounded-2xl border border-slate-200 bg-white p-5"
      }
    >
      <div
        className={
          accent ? "text-xs font-medium text-emerald-700" : "text-xs font-medium text-slate-500"
        }
      >
        {label}
      </div>
      <div
        className={
          accent
            ? "mt-2 text-3xl font-semibold tracking-tight text-emerald-900"
            : "mt-2 text-3xl font-semibold tracking-tight text-slate-900"
        }
      >
        {value}
      </div>
    </div>
  );
}

function ReviewRow({
  review,
  expanded,
  onToggle,
}: {
  review: DemoReview;
  expanded: boolean;
  onToggle: () => void;
}) {
  const meta = STATUS_META[review.status];
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start gap-4 px-5 py-4 text-left hover:bg-slate-50"
      >
        <div className="flex w-32 shrink-0 flex-col items-start gap-1">
          <StarRating rating={review.rating} />
          <span className="font-mono text-[11px] text-slate-500">
            {review.asin}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-3">
            <span className="font-medium text-slate-900">
              {review.productTitle}
            </span>
            <span className="text-xs text-slate-500">
              {review.verifiedPurchase ? "Verified" : "Unverified"} ·{" "}
              {review.reviewerName}
            </span>
          </div>
          <p className="mt-1 line-clamp-1 text-sm text-slate-600">
            "{review.bodySnippet}"
          </p>
          <div className="mt-2">
            <ViolationBadge type={review.violationType} />
          </div>
        </div>
        <div className="ml-2 flex shrink-0 flex-col items-end gap-1">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${meta.cls}`}
          >
            <meta.Icon className="h-3.5 w-3.5" />
            {meta.label}
          </span>
          <span className="text-xs text-slate-500">{review.statusUpdatedAt}</span>
        </div>
      </button>
      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50 px-5 py-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Full review
              </div>
              <p className="mt-2 text-sm text-slate-700">"{review.fullBody}"</p>
              <div className="mt-4 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Why we flagged this
              </div>
              <p className="mt-2 text-sm text-slate-700">{review.rationale}</p>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Takedown timeline
              </div>
              <ol className="mt-2 space-y-2.5">
                {review.timeline.map((step, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm">
                    <span
                      className={`mt-1 inline-block h-2 w-2 shrink-0 rounded-full ${
                        step.done ? "bg-emerald-500" : "bg-slate-300"
                      }`}
                    />
                    <span className="flex-1">
                      <span
                        className={
                          step.done ? "text-slate-900" : "text-slate-500"
                        }
                      >
                        {step.label}
                      </span>
                      <span className="block text-xs text-slate-500">
                        {step.at}
                      </span>
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StarRating({ rating }: { rating: number }) {
  const safe = Math.max(0, Math.min(5, Math.round(rating)));
  return (
    <span
      className="inline-flex items-center gap-0.5"
      aria-label={`${safe} out of 5 stars`}
    >
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`h-3.5 w-3.5 ${
            n <= safe
              ? "fill-amber-400 text-amber-400"
              : "fill-slate-200 text-slate-200"
          }`}
        />
      ))}
    </span>
  );
}
